import { useEffect, useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";

const ERROR_MESSAGES: Record<string, string> = {
  account_not_found: "No account found. Please contact support to get access.",
  identity_conflict: "This email is already linked to another account.",
  identity_email_missing: "WorkOS did not provide an email for this account. Please use sign up or another method.",
  authentication_failed: "Authentication failed. Please try again.",
};

interface LoginPageProps {
  mode?: "signin" | "signup";
}

export default function LoginPage({ mode = "signin" }: LoginPageProps) {
  const { login, signup, logout, loading } = useAuth();
  const search = useSearch({ strict: false }) as { error?: string };
  const [authError, setAuthError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  const urlError = search.error;
  const errorMessage = urlError ? ERROR_MESSAGES[urlError] ?? "Authentication failed." : null;
  const ctaLabel = mode === "signup" ? "Continue to WorkOS Sign Up" : "Continue to WorkOS Sign In";

  useEffect(() => {
    if (loading || started || !!urlError) {
      return;
    }

    setStarted(true);
    const action = mode === "signup" ? signup : login;
    void action().catch((error) => {
      console.error("Failed to start WorkOS authentication:", error);
      setAuthError("Failed to start authentication. Please try again.");
      setStarted(false);
    });
  }, [loading, started, urlError, mode, login, signup]);

  if (!errorMessage && !authError) {
    return <div className="min-h-screen bg-background" />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md p-8 space-y-6 text-center">
        <div className="flex flex-col items-center gap-3">
          <img
            src="/wealthscale_light.svg"
            alt="WealthScale Logo"
            width={88}
            height={88}
            className="block dark:hidden"
          />
          <img
            src="/wealthscale_dark.svg"
            alt="WealthScale Logo"
            width={88}
            height={88}
            className="hidden dark:block"
          />
          <h1 className="text-2xl font-semibold text-foreground">Authentication</h1>
          <p className="text-sm text-muted-foreground">Continue with WorkOS.</p>
        </div>

        {(errorMessage || authError) && (
          <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 flex items-start gap-2 text-left">
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <p className="text-sm text-destructive">{errorMessage ?? authError}</p>
          </div>
        )}

        <p className="text-sm text-muted-foreground">Click below to continue.</p>

        <Button
          variant="outline"
          type="button"
          className="w-full"
          onClick={() => {
            setAuthError(null);
            const action = mode === "signup" ? signup : login;
            void action().catch((error) => {
              console.error("Failed to start WorkOS authentication:", error);
              setAuthError("Failed to start authentication. Please try again.");
            });
          }}
          disabled={loading}
        >
          {ctaLabel}
        </Button>
        {(errorMessage || authError) && (
          <Button
            variant="ghost"
            type="button"
            className="w-full"
            onClick={() => {
              void logout().catch((error) => {
                console.error("Failed to clear WorkOS session:", error);
                setAuthError("Failed to clear session. Please try again.");
              });
            }}
          >
            Clear WorkOS session
          </Button>
        )}
      </Card>
    </div>
  );
}
